import { motion } from 'framer-motion';
import { CheckCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface OrderSuccessModalProps {
  orderNumber: string;
  onClose: () => void;
}

export default function OrderSuccessModal({ orderNumber, onClose }: OrderSuccessModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleContinueShopping = () => {
    onClose();
    navigate('/shop');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 20 }}
        transition={{ type: "spring", duration: 0.5 }}
        className="relative bg-gradient-to-br from-gray-900 to-gray-800 border border-green-500/30 rounded-2xl shadow-2xl max-w-md w-full p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="text-center space-y-6">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="flex justify-center"
          >
            <div className="relative">
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 0.8, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute inset-0 bg-green-500/20 rounded-full blur-xl"
              />
              <CheckCircle className="w-24 h-24 text-green-500 relative z-10" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-2"
          >
            <h2 className="text-3xl font-bold text-white">
              {t('shop.orderSent')}
            </h2>
            <p className="text-gray-300">
              {t('shop.orderCreatedSuccessfully')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
            className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 space-y-3"
          >
            <p className="text-sm text-gray-400 uppercase tracking-wider">
              {t('shop.orderNumberLabel')}
            </p>
            <p className="text-2xl font-mono font-bold text-green-400">
              {orderNumber}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="space-y-3 text-sm text-gray-300"
          >
            <p className="leading-relaxed">
              <strong className="text-white">{t('shop.paymentDetailsSent')}</strong>
            </p>
            <p className="leading-relaxed">
              {t('shop.paymentDeadline')} <strong className="text-green-400">{t('shop.workingDays')}</strong>.
            </p>
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 space-y-2">
              <p className="text-green-400 font-semibold">
                {t('shop.accountNumber')} 1352505023/0800
              </p>
              <p className="text-green-400">
                {t('shop.variableSymbol')} <span className="font-mono font-bold">{orderNumber}</span>
              </p>
            </div>
          </motion.div>

          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            onClick={handleContinueShopping}
            className="w-full bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-green-500/50"
          >
            {t('shop.continuesShopping')}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
